import React, { FunctionComponent, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@blueprintjs/core';

type ClickCallback =
  ((event: React.MouseEvent<HTMLElement, MouseEvent>) => void) &
  ((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void);

type Props = {
  isEditing: boolean,
  isNewFormButtonsLayout?: boolean
  disableApply?: boolean
  onClickCancel?: ClickCallback
  onClickSave?: ClickCallback
  onClickEdit?: ClickCallback
  onClickClose?: ClickCallback
  onClickSaveAndCopy?: ClickCallback
  onClickCopy?: ClickCallback
  onClickDelete?: ClickCallback
  onClickApply?: ClickCallback
  onClickExport?: ClickCallback
}

export const FormButtons: FunctionComponent<Props> = ({
  isEditing,
  isNewFormButtonsLayout = false,
  disableApply,
  onClickSave,
  onClickCancel,
  onClickEdit,
  onClickClose,
  onClickSaveAndCopy,
  onClickCopy,
  onClickDelete,
  onClickApply,
  onClickExport
}: Props) => {
  const { t } = useTranslation();

  const renderSaveAndCopyButton = () => {
    if (!onClickSaveAndCopy) {
      return null;
    }

    return (
      <Button
        data-testid="FormButtonsSaveAndCopy"
        className="actionButton"
        text={t('common.button.saveAndCopy')}
        intent="none"
        onClick={onClickSaveAndCopy}
      />
    );
  };

  const renderApply = () => {
    if (!onClickApply) {
      return null;
    }

    return (
      <Button
        data-testid="FormButtonsApply"
        className="actionButton"
        text={t('common.button.apply')}
        intent="none"
        disabled={!disableApply}
        onClick={onClickApply}
      />
    );
  };

  const renderButtons = () => {
    if (isEditing) {
      return (
        <Fragment>
          {onClickCancel != null && (
            <Button
              data-testid="FormButtonsCancel"
              className="actionButton"
              text={t('common.button.cancel')}
              intent="none"
              onClick={onClickCancel}
            />
          )}
          {renderSaveAndCopyButton()}
          {renderApply()}
          <Button
            data-testid="FormButtonsSave"
            className="actionButton"
            text={t('common.button.save')}
            intent="primary"
            onClick={onClickSave}
            disabled={onClickSave == null}
          />
        </Fragment>
      );
    }

    if (isNewFormButtonsLayout) {
      return (
        <Fragment>
          {onClickCancel != null && (
            <Button
              data-testid="FormButtonsCancel"
              className="actionButton"
              text={t('common.button.cancel')}
              intent="none"
              onClick={onClickCancel}
            />
          )}
          {onClickClose != null && (
            <Button
              data-testid="FormButtonsBack"
              className="actionButton"
              text={t('common.button.back')}
              intent="primary"
              onClick={onClickClose}
            />
          )}
        </Fragment>
      );
    }

    return (
      <Fragment>

        {onClickDelete != null && (
          <Button
            data-testid="FormButtonsDelete"
            className="actionButton"
            text={t('common.button.delete')}
            intent="none"
            onClick={onClickDelete}
          />
        )}
        {onClickCopy != null && (
          <Button
            data-testid="FormButtonsCopy"
            className="actionButton"
            text={t('common.button.copy')}
            intent="none"
            onClick={onClickCopy}
          />
        )}
        {onClickEdit != null && (
          <Button
            data-testid="FormButtonsEdit"
            className="actionButton"
            text={t('common.button.edit')}
            intent="none"
            onClick={onClickEdit}
          />
        )}
        {onClickClose != null && (
          <Button
            data-testid="FormButtonsBack"
            className="actionButton"
            text={t('common.button.back')}
            intent="primary"
            onClick={onClickClose}
          />
        )}
        {onClickExport != null && (
          <Button
            disabled // TODO: enable when doing network toplogy export feature
            data-testid="FormButtonskExport"
            className="actionButton"
            text={t('common.button.export')}
            intent="none"
            onClick={onClickExport}
          />
        )}
      </Fragment>
    );
  };

  return (
    <Fragment>
      {renderButtons()}
    </Fragment>
  );
};

export default FormButtons;
